import { Button, Modal, InputDeprecated, InlineAlert } from '@/components';
import { useForm } from 'react-hook-form';
import { R } from '@/features/programs/resources/ProgramsResources.resx';

import * as React from 'react';
import { useAtom } from 'jotai/index';
import { upsertNnWeightLogAtom } from '../model/upsertNnWeightLogAtom';
import { WeightLogRequest } from '@/shared/interfaces/api/sundvaegt-interfaces';
import { formatDateForInput } from '@/shared/helpers/formatDateForInput';

export type AddWeightLogModalProps = {
    onClose: () => void;
    minDate: Date;
};

export type IFormWeightLog = Omit<WeightLogRequest, 'ProgramId'>;

export const AddWeightLogModal = (props: AddWeightLogModalProps) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<IFormWeightLog>({
        mode: 'onSubmit',
        defaultValues: { WeightKg: null, Date: formatDateForInput(new Date()) },
    });

    const [{ mutate, status, reset, error }] = useAtom(upsertNnWeightLogAtom);

    // Extra state to again hide the validation errors when the user types
    const [showErrors, setShowErrors] = React.useState(false);

    const onSubmit = (data: IFormWeightLog) => {
        mutate(data, {
            onSuccess: () => {
                props.onClose();
            },
            onError: () => {
                reset();
            },
        });
    };

    const onError = (errors: any) => {
        setShowErrors(true);
    };

    return (
        <Modal
            onClose={() => {
                reset();
                props.onClose();
            }}
            title={R.SundvaegtAddWeightLogModalTitle}
        >
            {status !== 'error' && (
                <>
                    <form onSubmit={handleSubmit(onSubmit, onError)}>
                        <InputDeprecated
                            type="date"
                            min={formatDateForInput(props.minDate)}
                            max={formatDateForInput(new Date())}
                            label={R.SundvaegtDateInputLabel}
                            inputClassName={`${showErrors && errors.Date ? 'border border-secondary-red' : ''}`}
                            containerClassName="mt-8"
                            {...register('Date', {
                                required: R.SundvaegtDateInputRequiredLabel,
                                onChange: () => setShowErrors(false),
                            })}
                            error={
                                showErrors ? errors.Date?.message : undefined
                            }
                        />

                        <InputDeprecated
                            type="number"
                            step="0.1"
                            suffix="kg"
                            placeholder={
                                R.SundvaegtAddWeightLogInputPlaceholder
                            }
                            label={R.SundvaegtAddWeightLogModalInputLabel}
                            inputClassName={`${showErrors && errors.WeightKg ? 'border border-secondary-red' : ''}`}
                            containerClassName="mt-2"
                            {...register('WeightKg', {
                                required:
                                    R.SundvaegtAddWeightLogModalInputRequiredLabel,
                                min: {
                                    value: 40,
                                    message:
                                        R.SundvaegtPleaseEnterValidWeightLabel,
                                },
                                max: {
                                    value: 400,
                                    message:
                                        R.SundvaegtPleaseEnterValidWeightLabel,
                                },
                                onChange: () => setShowErrors(false),
                            })}
                            error={
                                showErrors
                                    ? errors.WeightKg?.message
                                    : undefined
                            }
                        />
                        <div className="mt-8 flex w-full flex-row justify-end">
                            <Button variant="primary" type="submit">
                                {R.SundvaegtAddWeightLogModalSubmitButton}
                            </Button>
                        </div>
                    </form>
                </>
            )}
            {status === 'error' && (
                <InlineAlert onClose={reset} errorMessage={error?.message} />
            )}
        </Modal>
    );
};
